import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { useDeviceContext } from 'pages/Unitlookup/stores/TelematicsDevice/TelematicsDeviceProvider';
import { DeviceStateApi, WifiProfile } from '../../stores/TelematicsDevice/DeviceStateApi';
import { isFirmwareGreaterThanOrEqualTo } from '../../stores/TelematicsDevice/FirmwareComparison';
import { withOktaAuth } from '@okta/okta-react';
import { IOktaContext } from '@okta/okta-react/bundles/types/OktaContext';
import { useAnalytics } from 'components/analytics/analytics';
import { AnalyticsEvents } from 'components/analytics/analyticsEvents';
import { TUSpinnerProgress } from 'components/TUSpinnerProgress';
import { WifiScanProvider } from '../../stores/WifiScan/WifiScanProvider';
import { SavedNetworksList } from './SavedNetworksList';
import { PendingNetworksList } from './PendingNetworksList';
import { AddNetworkSection } from './AddNetworkSection';
import {
  FIRMWARE_REQUIREMENT_MESSAGE,
  MIN_WIFI_FIRMWARE,
} from './wifiSetupModel';

export const WifiSetupButton: React.FC<IOktaContext> = (props) => {
  const { identity, state, reloadState, isGriffinDevice } = useDeviceContext();
  const analytics = useAnalytics();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [showModal, setShowModal] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showProgress, setShowProgress] = React.useState(false);
  const [wasSuccess, setWasSuccess] = React.useState(false);
  const [wasError, setWasError] = React.useState(false);

  // Networks queued to add or remove, applied together on save
  const [profilesToAdd, setProfilesToAdd] = React.useState<WifiProfile[]>([]);
  const [profilesToRemove, setProfilesToRemove] = React.useState<string[]>([]);

  const wifiProfiles = state.desired.wifi?.profiles;
  const existingProfiles = Array.isArray(wifiProfiles)
    ? wifiProfiles.filter(p => !p._delete)
    : [];

  const firmwareVersion = state.reported?.firmware?.version;
  const isFirmwareSupported = isFirmwareGreaterThanOrEqualTo(
    firmwareVersion, MIN_WIFI_FIRMWARE.major, MIN_WIFI_FIRMWARE.minor, MIN_WIFI_FIRMWARE.patch
  );

  const reportedWifiProfiles = state.reported?.wifi?.profiles;
  const reportedSsids = new Set(
    Array.isArray(reportedWifiProfiles)
      ? reportedWifiProfiles.filter(p => !p._delete).map(p => p.ssid)
      : []
  );

  if (!isGriffinDevice(identity)) {
    return null;
  }

  const handleOpen = () => {
    setShowModal(true);
    setProfilesToAdd([]);
    setProfilesToRemove([]);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleAddNetwork = (profile: WifiProfile) => {
    setProfilesToAdd(prev => [...prev, profile]);
  };

  const handleRemoveNewProfile = (index: number) => {
    setProfilesToAdd(profilesToAdd.filter((_, i) => i !== index));
  };

  const handleRemoveExistingProfile = (ssid: string) => {
    if (!profilesToRemove.includes(ssid)) {
      setProfilesToRemove([...profilesToRemove, ssid]);
    }
  };

  const handleUndoRemove = (ssid: string) => {
    setProfilesToRemove(profilesToRemove.filter(s => s !== ssid));
  };

  const handleProgressClose = () => {
    setShowProgress(false);
    if (wasSuccess) {
      handleClose();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setShowProgress(true);
    setWasSuccess(false);
    setWasError(false);

    try {
      const allProfiles: WifiProfile[] = [
        ...profilesToAdd,
        ...profilesToRemove.map(ssid => ({ ssid, _delete: true })),
      ];

      if (allProfiles.length > 0) {
        await DeviceStateApi.updateState(props.authState?.accessToken, identity.id, {
          wifi: {
            profiles: allProfiles,
          },
        });

        analytics.logEvent(AnalyticsEvents.WifiSetupSaved, {
          serialNumber: identity.serialNumber,
          addedCount: profilesToAdd.length,
          removedCount: profilesToRemove.length,
        });

        await reloadState();
      }

      setWasSuccess(true);
    } catch (error) {
      console.error('Failed to save Wi-Fi networks:', error);
      setWasError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = profilesToAdd.length > 0 || profilesToRemove.length > 0;

  return (
    <>
      <Tooltip title={isFirmwareSupported ? '' : FIRMWARE_REQUIREMENT_MESSAGE} arrow>
        <span
          tabIndex={!isFirmwareSupported ? 0 : undefined}
          aria-label={!isFirmwareSupported ? FIRMWARE_REQUIREMENT_MESSAGE : undefined}
        >
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={handleOpen}
            disabled={!isFirmwareSupported}
          >
            Manage Wi-Fi networks
          </Button>
        </span>
      </Tooltip>

      <TUSpinnerProgress
        open={showProgress}
        onClose={handleProgressClose}
        success={wasSuccess}
        error={wasError}
        successText="Networks saved successfully"
        errorText="There was an error while saving networks"
      />

      <Dialog
        open={showModal}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="wifi-setup-dialog-title"
      >
        <DialogTitle id="wifi-setup-dialog-title">Manage Saved Wi-Fi Networks</DialogTitle>
        <DialogContent>
          <SavedNetworksList
            profiles={existingProfiles}
            reportedSsids={reportedSsids}
            profilesToRemove={profilesToRemove}
            onRemove={handleRemoveExistingProfile}
            onUndoRemove={handleUndoRemove}
          />

          <PendingNetworksList profiles={profilesToAdd} onRemove={handleRemoveNewProfile} />

          <AddNetworkSection deviceId={identity.id} onAddNetwork={handleAddNetwork} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const WifiSetupButtonWithScanProvider: React.FC<IOktaContext> = (props) => (
  <WifiScanProvider>
    <WifiSetupButton {...props} />
  </WifiScanProvider>
);

export default withOktaAuth(WifiSetupButtonWithScanProvider);
